import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssessmentRowComponent } from '../assessment-row/assessment-row.component';
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { EmrCheckboxGroupComponent } from "../../../../shared/emr-checkbox-group/emr-checkbox-group.component";
import { EmrCheckboxComponent } from "../../../../shared/emr-checkbox/emr-checkbox.component";
import { FormOption } from 'src/app/shared/models/form-option.model';
import { MusculoskeletalFormGroup } from 'src/app/calvin-emr-shared/models/assessments/musculoskeletal.model';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { WnlGroupDirective } from '../wnl/wnl-group.directive';
import { WnlControlDirective } from '../wnl/wnl-control.directive';

@Component({
  selector: 'app-musculoskeletal-form',
  templateUrl: './musculoskeletal-form.component.html',
  styleUrls: ['./musculoskeletal-form.component.scss', '../assessments-tab.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    AssessmentRowComponent,
    EmrInputComponent,
    EmrCheckboxGroupComponent,
    EmrCheckboxComponent,
    WnlGroupDirective,
    WnlControlDirective,
  ],
})
export class MusculoskeletalFormComponent implements OnInit {

  formGroup = input.required<MusculoskeletalFormGroup>();
  patient = input.required<Patient>();
  @ViewChild(WnlGroupDirective) wnlGroup?: WnlGroupDirective;

  ngOnInit(): void {
    this.formGroup().controls.fallScore.setValue(this.computeFallRiskScore(), { emitEvent: false });
    this.formGroup().controls.HDtotalScore.setValue(this.computeHDSScore(), { emitEvent: false });

    // Add listener to changes in the fall risk form, which updates
    //  fall score each time a change occurs
    this.formGroup().valueChanges.subscribe(() => {
      this.formGroup().controls.fallScore.setValue(this.computeFallRiskScore(),
        { emitEvent: false });    // don't trigger the subscribe we are in!
      this.formGroup().controls.HDtotalScore.setValue(this.computeHDSScore(),
        { emitEvent: false });    // don't trigger the subscribe we are in!
      this.wnlGroup?.notifyStateChanged();
    });
  }

  reset() {
    this.formGroup().enable({ emitEvent: false });
  }

  adultFallRiskIsWnl = (score: any): boolean => Number(score) < 6;

  pediatricFallRiskIsWnl = (score: any): boolean => Number(score) < 12;

  /**
   * Fall Risk computations
   */
  computeAgeScore() {
    // if (!this.formGroup().controls.fallRiskAge) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskAge.value) {
      case '60 - 69 years (1 point)':
        return 1;
      case '70 - 79 years (2 points)':
        return 2;
      case 'greater than or equal to 80 years (3 points)':
        return 3;
      default:
        return 0;
    }
  }

  computeFallHistoryScore() {
    // if (!this.formGroup().controls.fallHistory) {
    //   return 0;
    // }
    return this.formGroup().controls.fallHistory.value ? 5 : 0;
  }

  computeEbuScore() {
    // if (!this.formGroup().controls.fallRiskEbu) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskEbu.value) {
      case 'Incontinence (2 points)':
        return 2;
      case 'Urgency or frequency (2 points)':
        return 2;
      case 'Urgency/frequency and incontinence (4 points)':
        return 4;
      default:
        return 0;
    }
  }

  computeMedicationsScore() {
    // if (!this.formGroup().controls.fallRiskMedications) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskMedications.value) {
      case 'On 1 high fall risk drug (3 points)':
        return 3;
      case 'On 2 or more high fall risk drugs (5 points)':
        return 5;
      case 'Sedated procedure within past 24 hours (7 points)':
        return 7;
      default:
        return 0;
    }
  }

  computePceScore() {
    // if (!this.formGroup().controls.fallRiskPce) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskPce.value) {
      case 'One present (1 point)':
        return 1;
      case 'Two present (2 points)':
        return 2;
      case '3 or more present (3 points)':
        return 3;
      default:
        return 0;
    }
  }

  computeMobilityFallRiskScore() {
    let score = 0;
    // if (!this.formGroup().controls.fallRiskMobility) {
    //   return score;
    // }
    const mobilityFallRiskFactors: string[] = this.formGroup().controls.fallRiskMobility.value || [];

    mobilityFallRiskFactors.forEach((factor) => {
      if (
        factor === this.MOBILITY_FALL_RISK_ASSISTANCE ||
        factor === this.MOBILITY_FALL_RISK_GAIT ||
        factor === this.MOBILITY_FALL_RISK_IMPAIRMENT
      ) {
        score += 2;
      }
    });

    return score;
  }

  computeCognitionScore() {
    let score = 0;
    // if (!this.formGroup().controls.fallRiskCognition) {
    //   return score;
    // }
    const cognitionFallRiskFactors: string[] = this.formGroup().controls.fallRiskCognition.value || [];

    cognitionFallRiskFactors.forEach((factor) => {
      if (factor === this.COGNITION_FALL_RISK_AWARENESS) {
        score += 1;
      } else if (factor === this.COGNITION_FALL_RISK_IMPULSIVE) {
        score += 2;
      } else if (factor === this.COGNITION_FALL_RISK_UNDERSTANDING) {
        score += 4;
      }
    });

    return score;
  }

  computeFallRiskScore() {
    return (
      this.computeAgeScore() +
      this.computeFallHistoryScore() +
      this.computeEbuScore() +
      this.computeMedicationsScore() +
      this.computePceScore() +
      this.computeMobilityFallRiskScore() +
      this.computeCognitionScore()
    );
  }

  computeFallRiskCategory(): string {
    if (this.computeFallRiskScore() < 6) {
      return 'Low';
    } else if (this.computeFallRiskScore() >= 6 && this.computeFallRiskScore() <= 13) {
      return 'Medium';
    } else {
      return 'High';
    }
  }

  mobilityAndTransfersOptions = [
    new FormOption('No assists'),
    new FormOption('Standby assist'),
    new FormOption('One assist'),
    new FormOption('Two assists'),
    new FormOption('Max assists'),
  ];

  deviceOptions = [
    new FormOption('Gait Belt'),
    new FormOption('Walker'),
    new FormOption('Cane'),
    new FormOption('Trapeze'),
  ];

  ageOptions = [
    new FormOption('60 - 69 years (1 point)'),
    new FormOption('70 - 79 years (2 points)'),
    new FormOption('greater than or equal to 80 years (3 points)'),
  ];

  fallHistoryOption = 'One fall within 6 months before admission (5 points)';

  ebuOptions = [
    new FormOption('Incontinence (2 points)'),
    new FormOption('Urgency or frequency (2 points)'),
    new FormOption('Urgency/frequency and incontinence (4 points)'),
  ];

  medicationsOptions = [
    new FormOption('On 1 high fall risk drug (3 points)'),
    new FormOption('On 2 or more high fall risk drugs (5 points)'),
    new FormOption('Sedated procedure within past 24 hours (7 points)'),
  ];

  pceOptions = [
    new FormOption('One present (1 point)'),
    new FormOption('Two present (2 points)'),
    new FormOption('3 or more present (3 points)'),
  ];

  MOBILITY_FALL_RISK_ASSISTANCE =
    'Requires assistance or supervision for mobility, transfer, or ambulation (2 points)';
  MOBILITY_FALL_RISK_GAIT = 'Unsteady gait (2 points)';
  MOBILITY_FALL_RISK_IMPAIRMENT =
    'Visual or auditory impairment affecting mobility (2 points)';
  mobilityFallRiskOptions = [
    new FormOption(this.MOBILITY_FALL_RISK_ASSISTANCE),
    new FormOption(this.MOBILITY_FALL_RISK_GAIT),
    new FormOption(this.MOBILITY_FALL_RISK_IMPAIRMENT),
  ];

  COGNITION_FALL_RISK_AWARENESS =
    'Altered awareness of immediate physical environment (1 point)';
  COGNITION_FALL_RISK_IMPULSIVE = 'Impulsive (2 points)';
  COGNITION_FALL_RISK_UNDERSTANDING =
    "Lack of understanding of one's physical and cognitive limitations (4 points)";
  cognitionOptions = [
    new FormOption(this.COGNITION_FALL_RISK_AWARENESS),
    new FormOption(this.COGNITION_FALL_RISK_IMPULSIVE),
    new FormOption(this.COGNITION_FALL_RISK_UNDERSTANDING),
  ];



  HDageOptions = [
    new FormOption('Less than 3 years old (4 points)'),
    new FormOption('3 less than 7 years old (3 points)'),
    new FormOption('7 to less than 13 years old (2 points)'),
    new FormOption('13 years and above (1 point)'),
  ];

  HDgenderOptions = [
    new FormOption('Male (2 points)'),
    new FormOption('Female (1 point)'),
  ];

  DIAGNOSIS_HUMPTY_DUMPTY_NEUROLOGICAL = 'Neurological Diagnosis (4 points)';
  DIAGNOSIS_HUMPTY_DUMPTY_ALTERATIONS =
    'Alterations in Oxygenation (Respiratory Diagnosis, Dehydration, Anemia, Anorexia, Syncope/Dyzziness, etc.) (3 points)';
  DIAGNOSIS_HUMPTY_DUMPTY_BEHAVIORAL = 'Psych/Behavioral Diagnosis (2 points)';
  DIAGNOSIS_HUMPTY_DUMPTY_OTHER = 'Other Diagnosis (1 points)';
  HDdiagnosisOptions = [
    new FormOption(this.DIAGNOSIS_HUMPTY_DUMPTY_NEUROLOGICAL),
    new FormOption(this.DIAGNOSIS_HUMPTY_DUMPTY_ALTERATIONS),
    new FormOption(this.DIAGNOSIS_HUMPTY_DUMPTY_BEHAVIORAL),
    new FormOption(this.DIAGNOSIS_HUMPTY_DUMPTY_OTHER),
  ];

  HDcognitiveImpairmentOptions = [
    new FormOption("Not Aware of Limitations (3 points)"),
    new FormOption('Forgets Limitations (2 point)'),
    new FormOption('Oriented to Own Ability (1 points)'),
  ];

  ENVIRONMENTAL_HUMPTY_DUMPTY_HISTORY = 'History of Falls or Infant-Toddler Placed in Bed (4 points)';
  ENVIRONMENTAL_HUMPTY_DUMPTY_ASSISTIVE = 'Patient uses assistive devices or Infant-Toddler Placed in Crib (3 points)';
  ENVIRONMENTAL_HUMPTY_DUMPTY_BED = 'Patient Placed in Bed (2 points)';
  ENVIRONMENTAL_HUMPTY_DUMPTY_OUTPATIENT = 'Outpatient Area (1 points)';
  HDenvironmentalFactorsOptions = [
    new FormOption(this.ENVIRONMENTAL_HUMPTY_DUMPTY_HISTORY),
    new FormOption(this.ENVIRONMENTAL_HUMPTY_DUMPTY_ASSISTIVE),
    new FormOption(this.ENVIRONMENTAL_HUMPTY_DUMPTY_BED),
    new FormOption(this.ENVIRONMENTAL_HUMPTY_DUMPTY_OUTPATIENT),
  ];

  HDresponseToSurgeryOptions = [
    new FormOption('Within 24 hours (3 points)'),
    new FormOption('Within 48 hours (2 points)'),
    new FormOption('More than 48 hours/None (1 points)'),
  ];

  MEDICATION_HUMPTY_DUMPTY_MULTIPLE = 
    "Multple usage of: Sedatives (excluding ICU patients sedated and paralyzed), Hynotics, Barbiturates, Phenothiazines, Antidepressants, Laxitives/Diuretics, Narcotics (3 points)";
  MEDICATION_HUMPTY_DUMPTY_ONE = "One of the meds listed above (2 points)";
  MEDICATION_HUMPTY_DUMPTY_NONE = "Other Medications/None (1 point)";
  HDmedicationUsageOptions = [
    new FormOption(this.MEDICATION_HUMPTY_DUMPTY_MULTIPLE),
    new FormOption(this.MEDICATION_HUMPTY_DUMPTY_ONE),
    new FormOption(this.MEDICATION_HUMPTY_DUMPTY_NONE),
  ];



  computeHDageScore() {
    // if (!this.formGroup().controls.HDage) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDage.value) {
      case 'Less than 3 years old (4 points)':
        return 4;
      case '3 less than 7 years old (3 points)':
        return 3;
      case '7 to less than 13 years old (2 points)':
        return 2;
      case '13 years and above (1 point)':
        return 1;
      default:
        return 0;
    }
  }

  computeHDgenderScore() {
    // if (!this.formGroup().controls.HDgender) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDgender.value) {
      case 'Male (2 points)':
        return 2;
      case 'Female (1 point)':
        return 1;
      default:
        return 0;
    }
  }

  computeHDdiagnosisScore() {
    // if (!this.formGroup().controls.HDdiagnosis) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDdiagnosis.value) {
      case this.DIAGNOSIS_HUMPTY_DUMPTY_NEUROLOGICAL:
        return 4;
      case this.DIAGNOSIS_HUMPTY_DUMPTY_ALTERATIONS:
        return 3;
      case this.DIAGNOSIS_HUMPTY_DUMPTY_BEHAVIORAL:
        return 2;
      case this.DIAGNOSIS_HUMPTY_DUMPTY_OTHER:
        return 1;
      default:
        return 0;
    }
  }

  computeHDcognitiveImpairmentScore() {
    // if (!this.formGroup().controls.HDcognitiveImpairment) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDcognitiveImpairment.value) {
      case "Not Aware of Limitations (3 points)":
        return 3;
      case 'Forgets Limitations (2 point)':
        return 2;
      case 'Oriented to Own Ability (1 points)':
        return 1;
      default:
        return 0;
    }
  }

  computeHDenvironmentalFactorsScore() {
    // if (!this.formGroup().controls.HDenvironmentalFactors) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDenvironmentalFactors.value) {
      case this.ENVIRONMENTAL_HUMPTY_DUMPTY_HISTORY:
        return 4;
      case this.ENVIRONMENTAL_HUMPTY_DUMPTY_ASSISTIVE:
        return 3;
      case this.ENVIRONMENTAL_HUMPTY_DUMPTY_BED:
        return 2;
      case this.ENVIRONMENTAL_HUMPTY_DUMPTY_OUTPATIENT:
        return 1;
      default:
        return 0;
    }
  }

  computeHDresponseToSurgeryScore() {
    // if (!this.formGroup().controls.HDresponseToSurgery) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDresponseToSurgery.value) {
      case 'Within 24 hours (3 points)':
        return 3;
      case 'Within 48 hours (2 points)':
        return 2;
      case 'More than 48 hours/None (1 points)':
        return 1;
      default:
        return 0;
    }
  }

  computeHDmedicationUsageScore() {
    // if (!this.formGroup().controls.HDmedicationUsage) {
    //   return 0;
    // }
    switch (this.formGroup().controls.HDmedicationUsage.value) {
      case this.MEDICATION_HUMPTY_DUMPTY_MULTIPLE:
        return 3;
      case this.MEDICATION_HUMPTY_DUMPTY_ONE:
        return 2;
      case this.MEDICATION_HUMPTY_DUMPTY_NONE:
        return 1;
      default:
        return 0;
    }
  }



  computeHDSScore() {
    return (
      this.computeHDageScore() +
      this.computeHDgenderScore() +
      this.computeHDdiagnosisScore() +
      this.computeHDcognitiveImpairmentScore() +
      this.computeHDenvironmentalFactorsScore() +
      this.computeHDresponseToSurgeryScore() +
      this.computeHDmedicationUsageScore()
    );
  }

  computeHDSCategory(): string {
    if (this.computeHDSScore() < 12) {
      return 'Not at Risk';
    } else {
      return 'At Risk';
    }
  }
}
